import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils/cn';

/** Small, unopinionated primitives. Feature components compose these; none of them fetch or parse. */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
};

const BUTTON_VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-900 shadow-sm disabled:bg-ink-300 disabled:text-ink-500',
  secondary:
    'border border-[var(--border)] bg-[var(--surface-raised)] text-strong hover:border-brand-400 hover:text-brand-600',
  ghost: 'text-muted hover:text-strong hover:bg-[var(--surface-sunken)]',
  danger: 'bg-alert-600 text-white hover:bg-alert-700',
};

const BUTTON_SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-[15px]',
};

export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
    />
  );
}

export function Card({
  children,
  className,
  as: Element = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article';
}) {
  return <Element className={cn('surface-card', className)}>{children}</Element>;
}

export function SectionHeading({
  title,
  description,
  action,
  id,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  id?: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 id={id} className="text-strong text-lg font-semibold tracking-tight">
          {title}
        </h2>
        {description ? <p className="text-muted mt-1 max-w-2xl text-sm">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export type Tone = 'safe' | 'found' | 'review' | 'info' | 'neutral';

const TONE_STYLES: Record<Tone, string> = {
  safe: 'bg-safe-50 text-safe-700 border-safe-200 dark:bg-safe-700/15 dark:text-safe-200',
  found: 'bg-warn-50 text-warn-700 border-warn-200 dark:bg-warn-700/15 dark:text-warn-200',
  review: 'bg-alert-50 text-alert-700 border-alert-200 dark:bg-alert-700/15 dark:text-alert-200',
  info: 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-700/15 dark:text-brand-200',
  neutral: 'bg-[var(--surface-sunken)] text-muted border-[var(--border)]',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE_STYLES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('rounded-lg border px-4 py-3 text-sm', TONE_STYLES[tone])}>
      {title ? <p className="font-semibold">{title}</p> : null}
      <div className={cn(title && 'mt-1', 'leading-relaxed')}>{children}</div>
    </div>
  );
}

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  description?: string;
  caution?: string;
};

export function Checkbox({ label, description, caution, className, ...props }: CheckboxProps) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border border-transparent p-3 transition-colors',
        'hover:border-[var(--border)] hover:bg-[var(--surface-sunken)]',
        className,
      )}
    >
      <input
        type="checkbox"
        {...props}
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded border-ink-300 accent-brand-600"
      />
      <span className="min-w-0">
        <span className="text-strong block text-sm font-medium">{label}</span>
        {description ? (
          <span className="text-muted mt-0.5 block text-[13px] leading-snug">{description}</span>
        ) : null}
        {caution && props.checked ? (
          <span className="text-warn-700 mt-1 block text-[13px] leading-snug">{caution}</span>
        ) : null}
      </span>
    </label>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div>
      <p className="text-muted text-xs font-medium tracking-wide uppercase">{label}</p>
      <p className="text-strong mt-1 text-lg font-semibold tabular-nums">{value}</p>
      {hint ? <p className="text-muted mt-0.5 text-xs">{hint}</p> : null}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string; count?: number }>;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="inline-flex gap-1 rounded-lg bg-[var(--surface-sunken)] p-1"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              selected ? 'bg-[var(--surface-raised)] text-strong shadow-sm' : 'text-muted hover:text-strong',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span className="text-muted ml-1.5 text-xs tabular-nums">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="text-muted py-6 text-center text-sm">{children}</p>;
}
